import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {AuthModel} from '../../auth/core/_models'
import {
  CodePurpose,
  CodeSent,
  CredentialsRequest,
  CredentialsResult,
  InfoRequest,
  ProfileError,
  ProfileSecurity,
  SignOutResult,
  TwoStepRequest,
} from './_models'

const PROFILE_URL = `${API_URL}/profile`

// Async functions, so that anything axios throws arrives as a rejected promise.
export const getProfileSecurity = async (signal?: AbortSignal): Promise<ProfileSecurity> =>
  (await axios.get<ProfileSecurity>(`${PROFILE_URL}/security`, {signal})).data

export const sendProfileCode = async (purpose: CodePurpose): Promise<CodeSent> =>
  (await axios.post<CodeSent>(`${PROFILE_URL}/security/code`, {purpose})).data

export const changeCredentials = async (request: CredentialsRequest): Promise<CredentialsResult> =>
  (await axios.post<CredentialsResult>(`${PROFILE_URL}/security/credentials`, request)).data

export const setTwoStep = async (request: TwoStepRequest): Promise<ProfileSecurity> =>
  (await axios.post<ProfileSecurity>(`${PROFILE_URL}/security/two-step`, request)).data

export const signOutSession = async (sessionId: string): Promise<SignOutResult> =>
  (
    await axios.post<SignOutResult>(
      `${PROFILE_URL}/sessions/${encodeURIComponent(sessionId)}/sign-out`,
      {}
    )
  ).data

export const signOutOtherSessions = async (): Promise<SignOutResult> =>
  (await axios.post<SignOutResult>(`${PROFILE_URL}/sessions/sign-out-others`, {})).data

export const updateProfileInfo = async (request: InfoRequest): Promise<AuthModel> =>
  (await axios.post<AuthModel>(`${PROFILE_URL}/info`, request)).data

export const uploadAvatar = async (
  photo: Blob,
  onProgress?: (percent: number) => void
): Promise<AuthModel> => {
  const form = new FormData()
  form.append('file', photo, 'avatar.jpg')
  const response = await axios.post<AuthModel>(`${PROFILE_URL}/avatar`, form, {
    headers: {'Content-Type': 'multipart/form-data'},
    onUploadProgress: (event: {loaded: number; total?: number}) => {
      if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100))
    },
  })
  return response.data
}

export const removeAvatar = async (): Promise<AuthModel> =>
  (await axios.post<AuthModel>(`${PROFILE_URL}/avatar/remove`, {})).data

export const getProfileSecurityQueryKey = (userId: number) => ['profile-security', userId]

type ErrorShape = {
  response?: {status?: number; data?: unknown; headers?: Record<string, string>}
  message?: string
}

// Never shows a raw server answer: only the server's own readable message, or the fallback.
export const readProfileError = (error: unknown, fallback: string): ProfileError => {
  const response = (error as ErrorShape | null)?.response
  const data =
    response?.data && typeof response.data === 'object'
      ? (response.data as {code?: unknown; message?: unknown; Message?: unknown})
      : undefined
  const message =
    typeof data?.message === 'string' && data.message.trim()
      ? data.message.trim()
      : !response
      ? 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.'
      : fallback
  const retryAfter = Number(response?.headers?.['retry-after'])
  return {
    status: Number(response?.status || 0),
    code: typeof data?.code === 'string' ? data.code : undefined,
    message,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  }
}

// A 4xx does not get better by retrying; a dropped connection may.
export const shouldRetryProfileRead = (failureCount: number, error: unknown) => {
  const status = Number((error as ErrorShape | null)?.response?.status || 0)
  if (status >= 400 && status < 500) return false
  return failureCount < 2
}
